// ════════════════════════════════════════════════════════════
//  skybox.js — THE SKY BOX: its dials (and the one sign/chat limit)
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-17: "I want a 'sky box' area where you and your friend can watch
//  the match perfectly but also can walk around and sit as if you're in the front
//  row both easily. Signs still accessible but here you can eventuallyyyy do way
//  more: emote, eat, drink, etc <3"
//
//  A private floating suite at the edge of the fight. You walk it as YOUR body
//  (Lyraei or Laostre — the Empire's two player bodies, gamemaster/MODELS.md),
//  sit in a front-row seat with one key, and friends who join sit beside you.
//  Every number caP'n may want to turn is here, tagged ⟵ DIAL (PRIME §1 law 17).
// ════════════════════════════════════════════════════════════

// ── ONE LIMIT FOR SIGNS AND THE WORLD CHAT ───────────────────
// caP'n: "put a cap to how much a user can write in the world chat, make it the same
// limit you can write on the signs. Then, when a player chats their chat message
// appears on the sign." So the sign's length IS the chat's length — one dial.
// 40 = what a held board still reads at from across the box (3 short lines).
export const SIGN = {
  max: 40,          // ⟵ DIAL longest sign AND longest world-chat message
  chatShow: 8,      // ⟵ DIAL seconds your chat message stays up on your sign
};

// ── WHERE THE BOX FLOATS ─────────────────────────────────────
// Angle is round the ring the same way the stands count it (x = sin a, z = cos a).
// 225° sits in a gap of the Close Deck (its islands are centred on 0/90/180/270°),
// well clear of the black hole (~130°). The box faces the middle of the plate.
// ⚠ Debris is kept out of the box's slice (Stands.jsx) — move the box, it follows.
export const SKY = {
  angleDeg: 225,    // ⟵ DIAL where round the ring
  r: 20.5,          // ⟵ DIAL distance from the middle of the plate to the box's middle (front edge ≈ r − d/2 = 17)
  y: 3.6,           // ⟵ DIAL floor height (the plate is at 0) — high enough to see over the fight, low enough to feel it
  w: 11,            // ⟵ DIAL width (along the rail)
  d: 7,             // ⟵ DIAL depth (rail to back)
  bob: 0.12,        // ⟵ DIAL how much it floats up and down (kept small: you're walking on it)
  seats: 6,         // ⟵ DIAL front-row seats = how many people fit in a box
  seatGap: 1.45,    // ⟵ DIAL shoulder to shoulder
  seatZ: 2.35,      // ⟵ DIAL how far forward the seats are (local z; the rail is at d/2)
  seatH: 0.5,       // ⟵ DIAL seat cushion height
  rail: 0.82,       // ⟵ DIAL height of the side and back glass
  railFront: 0.5,   // ⟵ DIAL height of the FRONT glass — low, so it never crosses a seated view of the fight (0.82 did, seen live)
  walk: 2.7,        // ⟵ DIAL walking speed (units/s)
  person: 1.5 / 1.3,// ⟵ DIAL the Pier's person scale: Lyraei 1.3 / Laostre 1.6 in Terror units × this
  cam: { dist: 3.9, pitch: 0.46, min: 2.2, max: 9, lift: 1.25 },   // ⟵ DIAL the walk-around camera behind you
  seatedEye: 0.42,  // ⟵ DIAL a seated eye's height above the cushion, as a share of the body's height
};
export const SKY_ANGLE = (SKY.angleDeg * Math.PI) / 180;
// loose debris (Stands.jsx) stays out of the box's slice: this half-angle, out to this far behind the box
export const SKY_CLEAR = { half: 0.55, pad: 6 };   // ⟵ DIAL
// the box's turn: its local +z (forward) points at the middle of the plate
export const SKY_TURN = SKY_ANGLE + Math.PI;
export const SKY_CENTER = { x: Math.sin(SKY_ANGLE) * SKY.r, z: Math.cos(SKY_ANGLE) * SKY.r };

// the Sky Box floats on its own slow swell (one formula: the floor, the bodies and the camera ride it)
export const skyBob = (t) => Math.sin(t * 0.33) * SKY.bob + Math.sin(t * 0.13 + 1.1) * SKY.bob * 0.5;

// box-local (x, z) → world (x, z). Local +z = toward the plate.
export function skyToWorld(lx, lz) {
  const c = Math.cos(SKY_TURN), s = Math.sin(SKY_TURN);
  return { x: SKY_CENTER.x + lx * c + lz * s, z: SKY_CENTER.z - lx * s + lz * c };
}
// world direction → box-local direction
export function worldDirToLocal(wx, wz) {
  const c = Math.cos(SKY_TURN), s = Math.sin(SKY_TURN);
  return { x: wx * c - wz * s, z: wx * s + wz * c };
}
// seat i's spot (box-local)
export const seatSpot = (i) => ({ x: (i - (SKY.seats - 1) / 2) * SKY.seatGap, z: SKY.seatZ });
// where you may walk (box-local), a little in from every edge
export const START = { x: 0, z: 0.35 };   // ⟵ DIAL where you step into the box (just behind the seats)
export const WALK = { x: SKY.w / 2 - 0.55, zMin: -SKY.d / 2 + 0.55, zMax: SKY.d / 2 - 0.6 };

// ── BODIES ───────────────────────────────────────────────────
// "f" = Lyraei, "m" = Laostre (Terror's rule: player_f IS Lyraei, player_m IS Laostre)
export const BODIES = { f: { h: 1.3, name: "Lyraei" }, m: { h: 1.6, name: "Laostre" } };
export const bodyH = (b) => (BODIES[b] || BODIES.f).h * SKY.person;
// sitting: the hips come down onto the cushion. `hip` = how high the hips stand, as a share of the
// body's height; `back` = how far back into the seat. Eyeballed from the rig — tune live.
export const SIT = { hip: 0.5, back: -0.12 };   // ⟵ DIAL
// how far a seated body is lowered so its hips sit on the cushion
export const seatLift = (h) => SKY.seatH + 0.04 - SIT.hip * h;

// ── THINGS TO DO — the seam for "emote, eat, drink, etc" ─────
// Each is a pose the body holds for `dur` seconds; everyone in the box sees it.
// Today: wave and cheer. Eat / drink come when there's something to hold (the bar at
// the back of the box is where they'll come from) — add a row here + a pose in
// three/skyBody.jsx (`poseFor`) and the button appears.
export const EMOTES = [
  { id: "wave", key: "1", dur: 2.6 },    // ⟵ DIAL
  { id: "cheer", key: "2", dur: 2.4 },   // ⟵ DIAL
];

// ── THE SIGN BOARDS — the seam for "you can buy different signs in the new shop" ──
// Every board is a colour set (board / ink / edge). `free` ones are yours today; the
// rest wait for the shop (it will flip ownership — nothing else here changes).
// 📌 caP'n: a few of these signs crossing over to Perry is "wayyyy later".
export const SIGN_BOARDS = [
  { id: "parchment", colors: ["#f3e3b3", "#2a1a3a", "#a98a52"], free: true },
  { id: "sun",       colors: ["#ffd84d", "#2b1b00", "#b8921a"], free: true },
  { id: "mint",      colors: ["#8be9c8", "#10323a", "#3a9d82"], free: true },
  { id: "rose",      colors: ["#ff9bc4", "#3a0f26", "#c4558a"], free: false },
  { id: "sky",       colors: ["#9fd8ff", "#10213f", "#4a8ac4"], free: false },
  { id: "violet",    colors: ["#c8b6ff", "#24124a", "#7d63c9"], free: false },
];
export const boardOf = (id) => SIGN_BOARDS.find((b) => b.id === id) || SIGN_BOARDS[0];

// ── THE NET ──────────────────────────────────────────────────
export const SKY_NET = {
  sendGap: 160,     // ⟵ DIAL ms between position updates while you move
  beat: 10000,      // ⟵ DIAL ms heartbeat while you stand still
  stale: 30000,     // ⟵ DIAL ms without a heartbeat → that person has gone
  whereStale: 12 * 3600 * 1000, // ⟵ DIAL ms: a friend's "where" older than this is a crashed ghost, not followed
  pubBoxes: 8,      // ⟵ DIAL how many public boxes one fight opens before the last one just gets crowded (caP'n 2026-10-18: strangers share a public box)
};
// THE TWO KINDS OF BOX (caP'n 2026-10-18: "whenever random people join and go to the skybox they go to a public
// skybox, but everyone has the option for a private one where they can invite friends")
export const BOX_MODES = ["public", "private"];   // the first is where you land until you pick

